/**
 * Payment Operations Service
 *
 * This service handles all payment operation API calls including:
 * - Creating new payments
 * - Retrying failed payments
 * - Refunding payments
 * - Approving/declining refund requests
 * - Canceling payments
 * - Capturing authorizations
 * - Extending authorizations
 * - Incrementing authorizations
 */

import ApiClient from './api-client'
import type { Payment } from '@shared/types/payment.types'
import type {
  RetryPaymentFormData,
  RefundPaymentFormData,
  ApproveRefundFormData,
  CancelPaymentFormData,
  CapturePaymentFormData,
  ExtendAuthFormData,
  IncrementAuthFormData,
  FlagFormData,
  SonarListFormData,
  DisputeResponseFormData,
  SendReceiptFormData,
} from '@features/payments/operations/shared/types/operation-types'
import type { CreatePaymentFormData } from '@features/payments/operations/create/types/create-payment.types'
import { createPayment as createMockPayment } from '@/mocks/generators/payments'

// Mock data for development
import { mockPayments } from '@shared/data/mockPayments'

/**
 * Service configuration
 */
interface PaymentOperationsServiceConfig {
  baseURL?: string
  apiKey?: string
  useMock?: boolean
}

/**
 * Payment Operations Service
 */
class PaymentOperationsService {
  private apiClient: ApiClient
  private readonly defaultHeaders: Record<string, string>
  private readonly useMock: boolean

  constructor(config: PaymentOperationsServiceConfig = {}) {
    this.apiClient = new ApiClient(config.baseURL || '')
    this.defaultHeaders = {
      'X-API-Key': config.apiKey || '',
      'Content-Type': 'application/json',
    }
    this.useMock = config.useMock ?? true // Default to mock during development
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Helper Methods
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Simulate API delay for mock responses
   */
  private async simulateDelay(ms: number = 1500): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Find payment by ID from mock data
   */
  private findMockPayment(paymentId: string): Payment | undefined {
    return mockPayments.find((p) => p.id === paymentId)
  }

  /**
   * Create mock updated payment
   */
  private createMockUpdatedPayment(
    originalPayment: Payment,
    updates: Partial<Payment>
  ): Payment {
    return {
      ...originalPayment,
      ...updates,
      updated: new Date().toISOString(),
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Create Payment
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Create a new payment
   */
  async createPayment(data: CreatePaymentFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const last4 = data.paymentMethod.type === 'manual' && data.paymentMethod.cardNumber
        ? data.paymentMethod.cardNumber.slice(-4)
        : '****'

      return createMockPayment({
        status: 'pending',
        amount: { amount: data.amount, decimal: 2, currency: data.currency },
        currency: data.currency,
        description: data.description,
        customer: data.customer.id
          ? { id: data.customer.id, name: data.customer.name ?? '', email: data.customer.email ?? '' }
          : { id: `cus_${crypto.randomUUID()}`, name: data.customer.name ?? '', email: data.customer.email ?? '' },
        paymentMethod: { type: 'card', displayString: `Card ending in ${last4}`, last4, brand: 'visa' },
      })
    }

    const url = `/payments`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Retry Payment
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Retry a failed payment
   */
  async retryPayment(data: RetryPaymentFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: 'pending',
        amount: {
          amount: data.amount,
          decimal: data.amount / 100,
          currency: data.currency,
        },
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/retry`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Refund Payment
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Process a refund for a payment
   */
  async refundPayment(data: RefundPaymentFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay(data.refundType === 'instant' ? 500 : 2000)

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: data.amount === originalPayment.amount.amount ? 'refunded' : 'succeeded',
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/refund`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Approve/Decline Refund
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Approve or decline a refund request
   */
  async approveRefund(data: ApproveRefundFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: data.decision === 'approve' ? 'refunded' : 'succeeded',
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/approve-refund`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cancel Payment
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Cancel a payment authorization
   */
  async cancelPayment(data: CancelPaymentFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: 'canceled',
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/cancel`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Capture Authorization
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Capture an authorized payment
   */
  async capturePayment(data: CapturePaymentFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: 'succeeded',
        amount: {
          amount: data.captureAmount,
          decimal: data.captureAmount / 100,
          currency: originalPayment.amount.currency,
        },
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/capture`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Extend Authorization
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Extend an authorization period
   */
  async extendAuthorization(data: ExtendAuthFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      // In real implementation, this would update the authorization expiry
      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {})

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/extend-auth`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Increment Authorization
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Increment an authorization amount
   */
  async incrementAuthorization(data: IncrementAuthFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const newAmount = originalPayment.amount.amount + data.incrementAmount

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        amount: {
          amount: newAmount,
          decimal: newAmount / 100,
          currency: data.currency,
        },
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/increment-auth`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Flag Payment / Payment Method / Customer
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Flag a payment, payment method, and/or customer for risk review
   */
  async flagPayment(data: FlagFormData): Promise<void> {
    if (this.useMock) {
      await this.simulateDelay(800)
      return
    }

    await this.apiClient.post<void>(
      `/payments/${data.paymentId}/flag`,
      data,
      this.defaultHeaders
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Send Receipt / Invoice
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Send a receipt and/or invoice to a customer via email
   */
  async sendReceipt(data: SendReceiptFormData): Promise<void> {
    if (this.useMock) {
      await this.simulateDelay(800)
      return
    }

    await this.apiClient.post<void>(
      `/payments/${data.paymentId}/send-receipt`,
      data,
      this.defaultHeaders
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Sonar Allow/Block List
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Add a payment attribute to a Sonar allow or block list
   */
  async addToSonarList(data: SonarListFormData): Promise<void> {
    if (this.useMock) {
      await this.simulateDelay(800)
      return
    }

    const endpoint = data.listType === 'allow' ? 'allow-list' : 'block-list'
    await this.apiClient.post<void>(
      `/payments/${data.paymentId}/${endpoint}`,
      data,
      this.defaultHeaders
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Respond to Dispute
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Respond to a dispute by submitting evidence or accepting it
   */
  async respondToDispute(data: DisputeResponseFormData): Promise<Payment> {
    if (this.useMock) {
      await this.simulateDelay()

      const originalPayment = this.findMockPayment(data.paymentId)
      if (!originalPayment) {
        throw new Error(`Payment not found: ${data.paymentId}`)
      }

      const updatedPayment = this.createMockUpdatedPayment(originalPayment, {
        status: data.action === 'accept_dispute' ? 'refunded' : originalPayment.status,
      })

      return updatedPayment
    }

    const url = `/payments/${data.paymentId}/respond-dispute`
    return this.apiClient.post<Payment>(url, data, this.defaultHeaders)
  }
}

// Export singleton instance
export const paymentOperationsService = new PaymentOperationsService({
  useMock: true, // Set to false when API is ready
})

export default paymentOperationsService
